import React, { useState, useEffect } from 'react';
import { Card, Select, message } from 'antd';
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import api from '../utils/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const DataChart = ({ department, dataForms, submissions, initialDataType, initialFieldKey, initialCategoryValue }) => {
  const [selectedDataType, setSelectedDataType] = useState(initialDataType || null);
  const [chartData, setChartData] = useState(null);
  const [chartType, setChartType] = useState('line');
  const [listData, setListData] = useState(null);
  const [numericFieldCount, setNumericFieldCount] = useState(0); // 数值字段数量
  const [selectedFields, setSelectedFields] = useState([]); // 记录性数据选择的统计字段
  const [selectedCategoryField, setSelectedCategoryField] = useState(initialCategoryValue || null); // 选择的分类字段（用于按分类统计）
  const [indicatorTargets, setIndicatorTargets] = useState([]); // 指标目标数据
  
  // 定义列表型数据类型（主要是记录列表，可以选择分类字段或数值字段进行统计）
  const listDataTypes = [
    'research_teams',      // 省部级及以上科研团队
    'teaching_teams',      // 省部级及以上教学团队
    'research_papers',     // 近五年发表的代表性学术论文、专著
    'research_awards',     // 近五年获得的代表性科研奖励
    'teaching_awards',     // 近五年获得省部级及以上教学成果奖
    'research_projects',   // 近五年承担的代表性科研项目
    'research_platforms',  // 代表性重点实验室、基地、中心等平台
    'teaching_platforms',  // 省部级及以上教学平台
    'degree_application',  // 申请新增学位授权情况
    'degree_points',       // 现有学位点情况
    'graduate_teaching_awards', // 研究生院教学成果奖
    'graduate_courses',    // 研究生院代表性课程和专业
    'graduate_student_achievements', // 在校生代表性成果
    'graduate_platforms'   // 研究生院平台
  ];

  useEffect(() => {
    // 当部门或dataForms变化时，重置选择并清空目标数据
    if (!dataForms || dataForms.length === 0) {
      setSelectedDataType(null);
      setChartData(null);
      setIndicatorTargets([]); // 清空目标数据
      return;
    }
    // 如果提供了初始数据类型，使用它
    if (initialDataType && dataForms.find(f => f.key === initialDataType)) {
      setSelectedDataType(initialDataType);
      // 如果提供了初始字段，设置它
      if (initialFieldKey) {
        const formConfig = dataForms.find(f => f.key === initialDataType);
        if (formConfig) {
          const field = formConfig.fields?.find(f => f.key === initialFieldKey || f.label === initialFieldKey);
          if (field) {
            if (field.type === 'select' || field.type === 'text') {
              // 分类字段：使用initialCategoryValue或field.key
              setSelectedCategoryField(initialCategoryValue || field.key);
              setSelectedFields([]);
            } else if (field.type === 'number') {
              // 数值字段：添加到selectedFields
              setSelectedFields([field.key]);
              setSelectedCategoryField(null);
            }
          }
        }
      }
    } else if (!selectedDataType || !dataForms.find(f => f.key === selectedDataType)) {
    // 如果之前选择的数据类型不在新的dataForms中，或者还没有选择，设置为第一个
      // 默认选择第一个数据类型
      const firstDataType = dataForms[0]?.key;
      if (firstDataType) {
        setSelectedDataType(firstDataType);
      } else {
        setSelectedDataType(null);
        setChartData(null);
        setIndicatorTargets([]); // 清空目标数据
      }
    }
  }, [department, dataForms, initialDataType, initialFieldKey, initialCategoryValue]);

  // 当选择的数据类型变化时，自动选择默认的分类字段
  useEffect(() => {
    if (selectedDataType && listDataTypes.includes(selectedDataType)) {
      const formConfig = dataForms?.find(f => f.key === selectedDataType);
      // 如果提供了初始字段，优先使用它（但只在没有已选择字段时设置）
      if (initialFieldKey && initialCategoryValue && !selectedCategoryField && selectedFields.length === 0) {
        setSelectedCategoryField(initialCategoryValue);
      } else if (formConfig && !selectedCategoryField && selectedFields.length === 0) {
        // 定义不应该出现在统计图表中的字段
        const nonStatisticalFields = ['年份', 'year', '时间', '资助时间', '批准时间', '批准年月', '起讫时间', '授权批准时间', '获奖年度'];
        
        // 查找合适的分类字段（优先选择包含"类别"、"category"的字段）
        const categoryFields = formConfig.fields?.filter(f => 
          (f.type === 'select' || f.type === 'text') && 
          !nonStatisticalFields.includes(f.key) && 
          !f.computed
        ) || [];
        
        if (categoryFields.length > 0) {
          // 优先选择包含"类别"或"category"的字段
          const preferredField = categoryFields.find(f => 
            f.label.includes('类别') || f.key.toLowerCase().includes('category')
          ) || categoryFields[0]; // 如果没有找到，选择第一个
          
          setSelectedCategoryField(preferredField.key);
        }
      }
    }
  }, [selectedDataType, dataForms]);

  // 当部门变化时，清空目标数据和图表数据
  useEffect(() => {
    setIndicatorTargets([]);
    setChartData(null);
  }, [department?.id]);

  useEffect(() => {
    // 只有当dataForms准备好且包含selectedDataType时才加载目标数据
    if (selectedDataType && department && department.id && dataForms && dataForms.length > 0) {
      const formConfig = dataForms.find(f => f.key === selectedDataType);
      if (formConfig) {
        // 加载目标数据
        loadIndicatorTargets();
      }
    }
  }, [selectedDataType, department, dataForms, selectedFields, selectedCategoryField]);

  // 当indicatorTargets更新时，重新加载图表数据以包含目标线
  useEffect(() => {
    if (selectedDataType && department && department.id && dataForms && dataForms.length > 0) {
      const formConfig = dataForms.find(f => f.key === selectedDataType);
      if (formConfig) {
        // 无论indicatorTargets是否有数据，都重新加载图表数据
        // 这样确保目标线能正确显示或隐藏
        console.log('DataChart: indicatorTargets更新，重新加载图表数据', {
          indicatorTargetsCount: indicatorTargets.length,
          department_id: department.id,
          selectedDataType: selectedDataType
        });
        loadChartData();
      }
    }
  }, [indicatorTargets, selectedDataType, department, dataForms, selectedFields, selectedCategoryField]);

  // 加载指标目标数据
  const loadIndicatorTargets = async () => {
    if (!department || !department.id || !selectedDataType) {
      setIndicatorTargets([]);
      return Promise.resolve();
    }
    
    try {
      const response = await api.get(`/indicator-targets?department_id=${department.id}&data_type=${selectedDataType}`);
      const targets = response.data || [];
      console.log('DataChart: 加载的目标数据', {
        department_id: department.id,
        data_type: selectedDataType,
        targets: targets,
        targetsCount: targets.length
      });
      setIndicatorTargets(targets);
      return Promise.resolve();
    } catch (error) {
      console.error('加载指标目标失败:', error);
      // 不显示错误消息，因为可能没有设置目标
      setIndicatorTargets([]);
      return Promise.resolve();
    }
  };

  const loadChartData = async () => {
    if (!department || !department.id || !selectedDataType) {
      console.warn('DataChart: Missing department or selectedDataType', { department, selectedDataType });
      return;
    }
    
    // 检查dataForms是否准备好
    if (!dataForms || dataForms.length === 0) {
      console.warn('DataChart: dataForms not ready yet');
      return;
    }
    
    // 检查formConfig是否存在
    const formConfig = dataForms.find(f => f.key === selectedDataType);
    if (!formConfig) {
      console.warn('DataChart: Form config not found for', selectedDataType, 'in dataForms', dataForms);
      // 不显示警告，因为可能是数据还没准备好
      return;
    }
    
    // 保存formConfig到组件状态，以便在渲染列表时使用
    const currentFormConfig = formConfig;
    
    try {
      const response = await api.get(`/statistics/${department.id}/${selectedDataType}`);
      const stats = response.data || {};
      
      // 检查是否是列表型数据类型
      const isListType = listDataTypes.includes(selectedDataType);
      
      // 计算总记录数和收集所有记录
      let totalRecords = 0;
      const allRecords = [];
      Object.entries(stats).forEach(([year, yearData]) => {
        if (Array.isArray(yearData)) {
          totalRecords += yearData.length;
          yearData.forEach(record => {
            allRecords.push({ ...record, year: parseInt(year) });
          });
        } else if (yearData) {
          totalRecords += 1;
          allRecords.push({ ...yearData, year: parseInt(year) });
        }
      });
      
      // 如果是列表型数据类型，允许选择列进行统计
      if (isListType) {
        // 如果记录数少于3条，显示数据列表
        if (totalRecords < 3) {
          setListData(allRecords);
          setChartData(null);
          return;
        }
        // 如果选择了分类字段，按分类统计
        if (selectedCategoryField) {
          setListData(null);
          // 按分类字段和年份进行统计
          const categoryStats = {};
          const allCategories = new Set();
          
          allRecords.forEach(record => {
            const year = record.year;
            const categoryValue = record.data_content?.[selectedCategoryField] || '未知';
            allCategories.add(categoryValue);
            
            if (!categoryStats[year]) {
              categoryStats[year] = {};
            }
            categoryStats[year][categoryValue] = (categoryStats[year][categoryValue] || 0) + 1;
          });
          
          // 构建图表数据
          const sortedYears = Object.keys(categoryStats).map(y => parseInt(y)).sort((a, b) => a - b).map(y => String(y));
          const categoryArray = Array.from(allCategories).sort();
          
          const datasets = categoryArray.map((category, index) => {
            const data = sortedYears.map(year => categoryStats[year]?.[category] || 0);
            return {
              label: category,
              data: data,
              borderColor: `hsl(${index * 60}, 70%, 50%)`,
              backgroundColor: `hsla(${index * 60}, 70%, 50%, 0.1)`,
              tension: 0.1,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointBackgroundColor: `hsl(${index * 60}, 70%, 50%)`,
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            };
          });

          // 为分类字段添加目标线（如果有目标）
          const datasetsWithTargets = [...datasets];
          const formConfig = dataForms?.find(f => f.key === selectedDataType);
          const categoryField = formConfig?.fields?.find(f => f.key === selectedCategoryField);
          
          // 定义颜色数组，每个分类值使用一个颜色
          const categoryColors = [
            { border: 'rgb(255, 99, 132)', point: 'rgb(255, 99, 132)' },
            { border: 'rgb(54, 162, 235)', point: 'rgb(54, 162, 235)' },
            { border: 'rgb(255, 206, 86)', point: 'rgb(255, 206, 86)' },
            { border: 'rgb(75, 192, 192)', point: 'rgb(75, 192, 192)' },
            { border: 'rgb(153, 102, 255)', point: 'rgb(153, 102, 255)' },
            { border: 'rgb(255, 159, 64)', point: 'rgb(255, 159, 64)' },
            { border: 'rgb(199, 199, 199)', point: 'rgb(199, 199, 199)' },
            { border: 'rgb(83, 102, 255)', point: 'rgb(83, 102, 255)' }
          ];
          
          if (categoryField) {
            categoryArray.forEach((category, categoryIndex) => {
              // 查找该分类值的目标
              const categoryTargets = indicatorTargets
                .filter(target => 
                  target.field_key === selectedCategoryField &&
                  target.field_type === 'category' &&
                  target.category_value === category
                )
                .sort((a, b) => a.target_year - b.target_year);
              
              // 为每个分类值分配一个颜色和线条样式，同一分类值的所有目标合并为一条线
              const categoryColor = categoryColors[categoryIndex % categoryColors.length];
              const dashPattern = [5, 5]; // 所有同一分类值的目标使用相同的虚线样式
              
              if (categoryTargets.length > 0) {
                // 将同一分类值的所有目标合并为一条连续的目标线
                const targetData = sortedYears.map((yearStr) => {
                  const year = parseInt(yearStr);
                  
                  // 找到该年份对应的目标（找到目标年份小于等于当前年份，且下一个目标年份大于当前年份的目标）
                  const targetForYear = categoryTargets
                    .filter(t => year >= t.target_year)
                    .sort((a, b) => b.target_year - a.target_year)[0]; // 找到最接近且不超过当前年份的目标
                  
                  if (targetForYear) {
                    return targetForYear.target_value;
                  }
                  
                  return undefined; // 使用 undefined 而不是 null，Chart.js 处理更好
                });
                
                // 同一分类值的所有目标合并为一条线
                datasetsWithTargets.push({
                  label: `${category} 目标`,
                  data: targetData,
                  borderColor: categoryColor.border,
                  backgroundColor: 'transparent',
                  borderDash: dashPattern,
                  borderWidth: 2,
                  pointRadius: 3,
                  pointHoverRadius: 5,
                  pointBackgroundColor: categoryColor.point,
                  fill: false,
                  spanGaps: false // 不跳过null值，形成连续线
                });
              }
            });
          }
          
          setChartData({
            labels: sortedYears,
            datasets: datasetsWithTargets
          });
          setNumericFieldCount(datasets.length);
          return;
        }
        // 如果有选择的数值字段，进行统计
        if (selectedFields.length > 0) {
          setListData(null);
          // 继续处理统计逻辑，使用selectedFields
        } else {
          // 如果没有选择字段，显示数据列表
          setListData(allRecords);
          setChartData(null);
          return;
        }
      } else {
        setListData(null);
      }
      
      // 找到数值型字段（先定义，以便在聚合时使用）
      // 如果是记录性数据类型且选择了字段，使用选择的字段；否则使用所有数值字段
      // 定义不应该出现在统计图表中的字段（时间维度、描述性字段等）
      const nonStatisticalFields = ['年份', 'year', '时间', '资助时间', '批准时间', '批准年月', '起讫时间', '授权批准时间', '获奖年度'];
      
      let numericFields = [];
      if (isListType && selectedFields.length > 0) {
        // 使用选择的字段进行统计
        numericFields = formConfig?.fields?.filter(f => 
          selectedFields.includes(f.key) && 
          f.type === 'number' && 
          !nonStatisticalFields.includes(f.key) && 
          !f.computed
        ) || [];
      } else {
        // 使用所有数值字段
        numericFields = formConfig?.fields?.filter(f => 
          f.type === 'number' && 
          !nonStatisticalFields.includes(f.key) && 
          !f.computed
        ) || [];
      }
      
      // 处理统计数据 - 对于同一年有多条数据的情况，需要聚合处理
      // 将年份转换为数字进行排序，确保正确排序（例如2025应该在2024之后）
      const years = Object.keys(stats).map(y => parseInt(y)).sort((a, b) => a - b).map(y => String(y));
      const dataPoints = years.map(year => {
        const yearData = stats[year];
        if (Array.isArray(yearData) && yearData.length > 0) {
          // 如果同一年有多条数据，只使用最新审核通过的记录（按reviewed_at或submitted_at排序）
          // 后端已经确保每个年份只有一条最新记录，但前端也要处理以防万一
          let latestRecord = yearData[0];
          if (yearData.length > 1) {
            // 按审核时间降序排序，如果没有审核时间则按提交时间降序排序
            latestRecord = yearData.sort((a, b) => {
              const aTime = a.reviewed_at ? new Date(a.reviewed_at).getTime() : (a.submitted_at ? new Date(a.submitted_at).getTime() : 0);
              const bTime = b.reviewed_at ? new Date(b.reviewed_at).getTime() : (b.submitted_at ? new Date(b.submitted_at).getTime() : 0);
              return bTime - aTime; // 降序
            })[0];
          }
          
          // 直接使用最新记录的数据内容
          return latestRecord.data_content || {};
        } else if (yearData) {
          return yearData.data_content || {};
        }
        return {};
      });

      let datasets = [];
      
      if (numericFields.length === 0) {
        // 如果没有数值字段，统计每年的记录数量
        const yearCounts = years.map(year => {
          const yearData = stats[year];
          // 如果是数组，返回数组长度；否则返回1
          return Array.isArray(yearData) ? yearData.length : (yearData ? 1 : 0);
        });
        
        datasets = [{
          label: '记录数量',
          data: yearCounts,
          borderColor: 'hsl(0, 70%, 50%)',
          backgroundColor: 'hsla(0, 70%, 50%, 0.1)',
          tension: 0.1,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: 'hsl(0, 70%, 50%)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }];
      } else {
        // 构建图表数据，排除总和字段
        // 识别需要排除的字段：总和字段（字段名包含"总"或"total"的，且不是组成部分）
        const excludedFieldKeys = new Set();
        numericFields.forEach(field => {
          const label = field.label || '';
          const key = field.key || '';
          // 如果字段名或标签包含"总"，且不是组成部分（如"种"、"台"、"万元"等），则排除
          if ((label.includes('总') || key.toLowerCase().includes('total')) && 
              !label.includes('种') && !label.includes('台') && !label.includes('万元')) {
            excludedFieldKeys.add(field.key);
          }
        });
        
        // 过滤掉需要排除的字段
        const validFields = numericFields.filter(field => !excludedFieldKeys.has(field.key));
        
        datasets = validFields.map((field, index) => {
          let fieldLabel = field.label;
          let fieldData = dataPoints.map(point => point[field.key] || 0);
          
          return {
            label: fieldLabel,
            data: fieldData,
          borderColor: `hsl(${index * 60}, 70%, 50%)`,
          backgroundColor: `hsla(${index * 60}, 70%, 50%, 0.1)`,
          tension: 0.1,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: `hsl(${index * 60}, 70%, 50%)`,
          pointBorderColor: '#fff',
          pointBorderWidth: 2
          };
        });
      }

      // 确保所有数据集的数据点数量与年份数量一致
      // 对于没有数据的年份，使用0而不是null，这样图表会显示所有年份
      const validatedDatasets = datasets.map(dataset => ({
        ...dataset,
        data: years.map((year, index) => {
          const value = dataset.data[index];
          // 确保数据值有效，无效值使用0而不是null，这样所有年份都会显示
          if (value !== null && value !== undefined && !isNaN(value)) {
            return value;
          }
          return 0; // 使用0而不是null，确保所有年份都显示
        }),
        // 确保所有数据点都可见
        spanGaps: false, // 不跳过缺失的数据点
        pointRadius: 5, // 确保点足够大，可见
        pointHoverRadius: 7
      }));

      // 为每个字段添加目标线
      const datasetsWithTargets = [...validatedDatasets];
      
      console.log('DataChart: 准备添加目标线', {
        department_id: department?.id,
        department_name: department?.name,
        selectedDataType: selectedDataType,
        indicatorTargets: indicatorTargets,
        indicatorTargetsCount: indicatorTargets.length,
        numericFields: numericFields,
        numericFieldsCount: numericFields.length
      });
      
      // 为每个数值字段查找对应的目标（只在有validFields时）
      if (numericFields.length > 0) {
        const excludedFieldKeys = new Set();
        numericFields.forEach(field => {
          const label = field.label || '';
          const key = field.key || '';
          if ((label.includes('总') || key.toLowerCase().includes('total')) && 
              !label.includes('种') && !label.includes('台') && !label.includes('万元')) {
            excludedFieldKeys.add(field.key);
          }
        });
        const validFields = numericFields.filter(field => !excludedFieldKeys.has(field.key));
        
        // 定义颜色数组，每个字段使用一个颜色
        const fieldColors = [
          { border: 'rgb(255, 99, 132)', point: 'rgb(255, 99, 132)' },
          { border: 'rgb(54, 162, 235)', point: 'rgb(54, 162, 235)' },
          { border: 'rgb(255, 206, 86)', point: 'rgb(255, 206, 86)' },
          { border: 'rgb(75, 192, 192)', point: 'rgb(75, 192, 192)' },
          { border: 'rgb(153, 102, 255)', point: 'rgb(153, 102, 255)' },
          { border: 'rgb(255, 159, 64)', point: 'rgb(255, 159, 64)' },
          { border: 'rgb(199, 199, 199)', point: 'rgb(199, 199, 199)' },
          { border: 'rgb(83, 102, 255)', point: 'rgb(83, 102, 255)' }
        ];
        
        validFields.forEach((field, fieldIndex) => {
        // 查找该字段的所有目标（按年份排序）
        // 确保匹配 data_type、field_key 和 field_type
        const fieldTargets = indicatorTargets
          .filter(target => 
            target.data_type === selectedDataType &&
            target.field_key === field.key && 
            target.field_type === 'number' &&
            (!target.category_value || target.category_value === null)
          )
          .sort((a, b) => a.target_year - b.target_year);
        
        console.log('DataChart: 字段目标匹配', {
          fieldKey: field.key,
          fieldLabel: field.label,
          allTargets: indicatorTargets.length,
          fieldTargets: fieldTargets,
          fieldTargetsCount: fieldTargets.length
        });
        
        // 为每个字段分配一个颜色和线条样式，同一字段的所有目标合并为一条线
        const fieldColor = fieldColors[fieldIndex % fieldColors.length];
        const dashPattern = [5, 5]; // 所有同一字段的目标使用相同的虚线样式
        
        if (fieldTargets.length > 0) {
          // 将同一字段的所有目标合并为一条连续的目标线
          const targetData = years.map((yearStr) => {
            const year = parseInt(yearStr);
            
            // 找到该年份对应的目标（找到目标年份小于等于当前年份，且下一个目标年份大于当前年份的目标）
            const targetForYear = fieldTargets
              .filter(t => year >= t.target_year)
              .sort((a, b) => b.target_year - a.target_year)[0]; // 找到最接近且不超过当前年份的目标
            
            if (targetForYear) {
              return targetForYear.target_value;
            }
            
            return undefined; // 使用 undefined 而不是 null，Chart.js 处理更好
          });
          
          console.log('DataChart: 添加目标线', {
            fieldLabel: field.label,
            fieldKey: field.key,
            targetData: targetData,
            years: years
          });
          
          // 同一字段的所有目标合并为一条线
          datasetsWithTargets.push({
            label: `${field.label} 目标`,
            data: targetData,
            borderColor: fieldColor.border,
            backgroundColor: 'transparent',
            borderDash: dashPattern,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: fieldColor.point,
            fill: false,
            spanGaps: false // 不跳过null值，形成连续线
          });
        }
        });
      }
      
      setChartData({
        labels: years,
        datasets: datasetsWithTargets
      });
      
      // 记录数值字段数量，用于决定是否显示饼图选项
      // 如果没有数值字段，使用数据集数量（通常是1，表示记录数量）
      const fieldCount = numericFields.length > 0 ? numericFields.length : datasets.length;
      setNumericFieldCount(fieldCount);
      
      // 如果只有一个数据系列且当前选择的是饼图，切换到折线图
      if (fieldCount <= 1 && chartType === 'pie') {
        setChartType('line');
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
      if (error.response?.status === 404) {
        // 如果没有数据，设置空数据而不是显示错误
        setChartData(null);
        setListData([]);
      } else {
        message.error('加载统计数据失败: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const renderChart = () => {
    if (!chartData) {
      return <div style={{ textAlign: 'center', padding: '50px' }}>请选择数据类型</div>;
    }

    const commonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 0
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: dataForms?.find(f => f.key === selectedDataType)?.label || '数据统计'
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          filter: function(tooltipItem) {
            // 过滤掉 null 或 undefined 的数据点，避免工具提示错误
            const value = tooltipItem.parsed?.y ?? tooltipItem.parsed;
            return value !== null && value !== undefined && !isNaN(value);
          },
          callbacks: {
            title: function(context) {
              // 显示年份（x轴值）作为标题
              if (context && context.length > 0) {
                const label = context[0].label || '';
                // 如果label是年份格式，直接返回；否则尝试提取年份
                if (typeof label === 'string' && label.includes('年')) {
                  return label;
                }
                return `${label}年`;
              }
              return '';
            },
            label: function(context) {
              // 检查 parsed 是否为 null
              if (!context.parsed) {
                return '';
              }
              const value = context.parsed.y ?? context.parsed;
              // 允许显示0值，只过滤null、undefined和NaN
              if (value === null || value === undefined || isNaN(value)) {
                return '';
              }
              const label = context.dataset.label || '';
              // 格式化数值显示为2位小数
              const formattedValue = typeof value === 'number' 
                ? value.toFixed(2)
                : value;
              return `${label}: ${formattedValue}`;
            },
            afterLabel: function(context) {
              // 可以在这里添加额外的信息，比如单位
              return '';
            }
          }
        },
        // 数据标签插件配置 - 直接在数据点上显示数值
        datalabels: {
          anchor: 'end',
          align: 'top',
          formatter: (value) => {
            if (value === null || value === undefined || isNaN(value)) {
              return '';
            }
            return typeof value === 'number' ? value.toFixed(2) : value;
          },
          color: '#333',
          font: {
            weight: 'bold',
            size: 11
          },
          padding: {
            top: 4,
            bottom: 2,
            left: 2,
            right: 2
          },
          offset: 4, // 默认偏移量，各图表类型会覆盖
          clamp: true, // 限制标签在图表区域内
          clip: false
        }
      },
      scales: chartType !== 'pie' ? {
        x: {
          display: true,
          title: {
            display: true,
            text: '年份'
          },
          ticks: {
            autoSkip: false, // 不自动跳过任何标签
            maxRotation: 45,
            minRotation: 0,
            stepSize: 1, // 每一年都显示
            callback: function(value, index) {
              // 确保显示所有年份标签
              return this.getLabelForValue(value);
            }
          },
          // 确保所有年份都显示
          min: undefined,
          max: undefined
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: '数值'
          },
          ticks: {
            precision: 0
          }
        }
      } : undefined
    };

    switch (chartType) {
      case 'line':
        // 为折线图创建专门的选项，优化标签位置避免重叠
        const lineOptions = {
          ...commonOptions,
          plugins: {
            ...commonOptions.plugins,
            datalabels: {
              ...commonOptions.plugins.datalabels,
              anchor: 'end',
              align: 'top',
              offset: 8, // 增加偏移量，避免重叠
              clamp: true, // 限制标签在图表区域内
              clip: false
            }
          }
        };
        return (
          <div style={{ width: '100%', height: '400px', margin: 0, padding: 0 }}>
            <Line data={chartData} options={lineOptions} />
          </div>
        );
      case 'bar':
        // 为柱状图创建专门的选项，优化标签位置避免重叠
        const barOptions = {
          ...commonOptions,
          plugins: {
            ...commonOptions.plugins,
            datalabels: {
              ...commonOptions.plugins.datalabels,
              anchor: 'end',
              align: 'top',
              offset: 6, // 增加偏移量，避免重叠
              clamp: true, // 限制标签在图表区域内
              clip: false
            }
          }
        };
        return (
          <div style={{ width: '100%', height: '400px', margin: 0, padding: 0 }}>
            <Bar data={chartData} options={barOptions} />
          </div>
        );
      case 'pie':
        // 饼图：为每年显示一个饼图，展示该年所有字段的分布
        const generateColors = (count) => {
          const baseColors = [
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
            'rgba(199, 199, 199, 0.6)',
            'rgba(83, 102, 255, 0.6)',
            'rgba(255, 99, 255, 0.6)',
            'rgba(99, 255, 132, 0.6)',
            'rgba(255, 159, 132, 0.6)',
            'rgba(132, 99, 255, 0.6)',
          ];
          const colors = [];
          for (let i = 0; i < count; i++) {
            colors.push(baseColors[i % baseColors.length]);
          }
          return colors;
        };
        
        // 获取当前表单配置，用于识别计算字段和总和字段
        const formConfig = dataForms?.find(f => f.key === selectedDataType);
        const allFields = formConfig?.fields || [];
        
        // 识别需要排除的字段：
        // 1. 计算字段（computed字段）
        // 2. 总和字段（字段名包含"总"或"total"的，且是数值类型）
        const excludedFields = new Set();
        allFields.forEach(field => {
          if (field.computed) {
            excludedFields.add(field.key);
          } else if (field.type === 'number') {
            const label = field.label || '';
            const key = field.key || '';
            // 如果字段名或标签包含"总"，且不是组成部分，则排除
            if ((label.includes('总') || key.toLowerCase().includes('total')) && 
                !label.includes('种') && !label.includes('台')) {
              excludedFields.add(field.key);
            }
          }
        });
        
        const pieCharts = chartData.labels.map((year, yearIndex) => {
          // 获取该年所有字段的数据，排除总和字段、计算字段和目标线
          const yearData = chartData.datasets
            .map(dataset => {
              // 通过label找到对应的字段配置
              const field = allFields.find(f => f.label === dataset.label);
              return {
                label: dataset.label,
                key: field?.key || dataset.label,
                value: dataset.data[yearIndex] || 0,
                field: field
              };
            })
            .filter(item => {
              // 排除目标线（label包含"目标"的数据集）
              if (item.label && item.label.includes('目标')) {
                return false;
              }
              // 排除总和字段和计算字段
              if (item.field && excludedFields.has(item.field.key)) {
                return false;
              }
              // 只显示有数据的字段
              return item.value > 0;
            });
          
          if (yearData.length === 0) {
            return null; // 如果该年没有数据，不显示饼图
          }
          
          const colors = generateColors(yearData.length);
          const pieData = {
            labels: yearData.map(item => item.label),
            datasets: [{
              label: `${year}年数据`,
              data: yearData.map(item => item.value),
              backgroundColor: colors,
              borderColor: colors.map(color => color.replace('0.6', '1')), // 边框颜色更不透明
              borderWidth: 2
            }]
          };
          
          const pieOptions = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: 0
            },
            backgroundColor: 'transparent', // 图表背景透明，让Card的背景显示
            plugins: {
              legend: {
                display: true,
                position: 'right',
              },
              title: {
                display: true,
                text: `${year}年数据分布`,
                font: {
                  size: 16,
                  weight: 'bold'
                }
              },
              tooltip: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                callbacks: {
                  label: function(context) {
                    try {
                      // 检查 parsed 是否为 null 或 undefined
                      if (!context || !context.parsed) {
                        return '';
                      }
                    const label = context.label || '';
                    const value = context.parsed || 0;
                      if (value === null || value === undefined || isNaN(value)) {
                        return '';
                      }
                      const total = context.dataset.data.reduce((a, b) => (a || 0) + (b || 0), 0);
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    const formattedValue = typeof value === 'number' ? value.toFixed(2) : value;
                    return `${label}: ${formattedValue} (${percentage}%)`;
                    } catch (error) {
                      // 如果出现任何错误，返回空字符串
                      return '';
                    }
                  },
                  filter: function(tooltipItem) {
                    try {
                      // 过滤掉 null 或 undefined 的数据点
                      if (!tooltipItem || !tooltipItem.parsed) {
                        return false;
                      }
                      const value = tooltipItem.parsed;
                      return value !== null && value !== undefined && !isNaN(value);
                    } catch (error) {
                      return false;
                    }
                  }
                }
              },
              datalabels: {
                display: true,
                color: '#fff',
                font: {
                  weight: 'bold',
                  size: 11
                },
                formatter: (value, context) => {
                  // 始终显示数值，对于小比例部分显示数值，大比例部分显示百分比
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((value / total) * 100) : 0;
                  
                  // 如果比例小于3%，显示数值；否则显示百分比
                  if (percentage < 3) {
                    return typeof value === 'number' ? value.toFixed(2) : value.toString(); // 小比例部分显示数值（2位小数）
                  } else {
                    return `${percentage.toFixed(1)}%`; // 大比例部分显示百分比
                  }
                },
                // 添加文字描边，提高可读性
                textStrokeColor: '#000',
                textStrokeWidth: 2,
                // 允许标签显示在饼图外部（对于小比例部分）
                clamp: false,
                clip: false
              }
            }
          };
          
          // 根据数据计算一个主色调（用于Card背景的轻微着色）
          const totalValue = yearData.reduce((sum, item) => sum + item.value, 0);
          const dominantIndex = yearData.findIndex(item => item.value === Math.max(...yearData.map(d => d.value)));
          const dominantColor = colors[dominantIndex] || colors[0];
          // 提取RGB值并创建浅色背景
          const rgbMatch = dominantColor.match(/\d+/g);
          const bgColor = rgbMatch 
            ? `rgba(${rgbMatch[0]}, ${rgbMatch[1]}, ${rgbMatch[2]}, 0.05)` 
            : 'transparent';
          
          return (
            <Card 
              key={year} 
              title={`${year}年数据分布`}
              style={{ 
                width: '100%',
                border: `1px solid ${dominantColor.replace('0.6', '0.3')}`
              }}
              bodyStyle={{
                backgroundColor: bgColor,
                padding: '20px',
                margin: 0
              }}
            >
              <div style={{ 
                height: '350px', 
                width: '100%',
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                backgroundColor: 'transparent',
                margin: 0,
                padding: 0
              }}>
                <Pie data={pieData} options={pieOptions} plugins={[ChartDataLabels]} />
              </div>
            </Card>
          );
        }).filter(chart => chart !== null);
        
        if (pieCharts.length === 0) {
          return <div style={{ textAlign: 'center', padding: '50px' }}>暂无数据</div>;
        }
        
        return (
          <div style={{ 
            maxHeight: '700px',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '10px',
            width: '100%'
          }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '20px',
              width: '100%',
              boxSizing: 'border-box'
          }}>
            {pieCharts}
            </div>
          </div>
        );
      default:
        return (
          <div style={{ width: '100%', height: '400px', margin: 0, padding: 0 }}>
            <Line data={chartData} options={commonOptions} plugins={[ChartDataLabels]} />
          </div>
        );
    }
  };

  if (!department) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <p>请选择部门</p>
        </div>
      </Card>
    );
  }

  if (!dataForms || dataForms.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <p>该部门暂无可统计的数据类型</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <Select
          style={{ width: 300, marginRight: '10px' }}
          placeholder="选择数据类型"
          value={selectedDataType}
          onChange={(value) => {
            setSelectedDataType(value);
            setSelectedFields([]); // 切换数据类型时清空选择的字段
            setSelectedCategoryField(null); // 清空分类字段选择
          }}
        >
          {dataForms.map(form => (
            <Select.Option key={form.key} value={form.key}>
              {form.label}
            </Select.Option>
          ))}
        </Select>
        {/* 如果是记录性数据类型且有数据，显示字段选择器 */}
        {selectedDataType && listDataTypes.includes(selectedDataType) && (() => {
          const formConfig = dataForms?.find(f => f.key === selectedDataType);
          if (!formConfig) return null;
          
          // 定义不应该出现在统计图表中的字段
          const nonStatisticalFields = ['年份', 'year', '时间', '资助时间', '批准时间', '批准年月', '起讫时间', '授权批准时间', '获奖年度'];
          
          const numericFields = formConfig.fields?.filter(f => 
            f.type === 'number' && 
            !nonStatisticalFields.includes(f.key) && 
            !f.computed
          ) || [];
          const categoryFields = formConfig.fields?.filter(f => 
            (f.type === 'select' || f.type === 'text') && 
            !nonStatisticalFields.includes(f.key) && 
            !f.computed
          ) || [];
          
          return (
            <>
              {/* 分类字段选择器（用于按分类统计个数） */}
              {categoryFields.length > 0 && (
                <Select
                  style={{ width: 250, marginRight: '10px' }}
                  placeholder="选择分类字段（统计个数）"
                  value={selectedCategoryField}
                  onChange={(value) => {
                    setSelectedCategoryField(value);
                    setSelectedFields([]); // 清空数值字段选择
                  }}
                  allowClear
                >
                  {categoryFields.map(field => (
                    <Select.Option key={field.key} value={field.key}>
                      {field.label}（按分类统计）
                    </Select.Option>
                  ))}
                </Select>
              )}
              {/* 数值字段选择器 */}
              {numericFields.length > 0 && !selectedCategoryField && (
                <Select
                  mode="multiple"
                  style={{ width: 300, marginRight: '10px' }}
                  placeholder="选择要统计的字段（数值型）"
                  value={selectedFields}
                  onChange={(value) => {
                    setSelectedFields(value);
                    setSelectedCategoryField(null); // 清空分类字段选择
                  }}
                >
                  {numericFields.map(field => (
                    <Select.Option key={field.key} value={field.key}>
                      {field.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </>
          );
        })()}
        {chartData && !listData && (
          <Select
            style={{ width: 150 }}
            value={chartType}
            onChange={setChartType}
          >
            <Select.Option value="line">折线图</Select.Option>
            <Select.Option value="bar">柱状图</Select.Option>
            {numericFieldCount > 1 && (
              <Select.Option value="pie">饼图</Select.Option>
            )}
          </Select>
        )}
      </div>
      {listData && (!listDataTypes.includes(selectedDataType) || selectedFields.length === 0) ? (
        <div style={{ marginTop: '20px' }}>
          <h3>数据列表（共 {listData.length} 条记录）</h3>
          {listDataTypes.includes(selectedDataType) && listData.length >= 3 && !selectedCategoryField && selectedFields.length === 0 && (
            <div style={{ marginBottom: '10px', color: '#666', padding: '10px', background: '#f0f0f0', borderRadius: '4px' }}>
              <p>💡 提示：请在上方选择分类字段（统计个数）或数值字段，系统将自动生成统计图表</p>
            </div>
          )}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {listData.map((record, index) => {
              const formConfig = dataForms?.find(f => f.key === selectedDataType);
              return (
                <Card key={index} size="small" style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                    {Object.entries(record.data_content || {}).map(([key, value]) => {
                      const field = formConfig?.fields?.find(f => f.key === key);
                      if (!field || field.computed) return null;
                      return (
                        <div key={key}>
                          <strong>{field.label || key}:</strong> {value || '-'}
                        </div>
                      );
                    })}
                    {record.year && (
                      <div>
                        <strong>年份:</strong> {record.year}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ 
          width: '100%', 
          margin: 0, 
          padding: 0,
          ...(chartType === 'pie' ? { minHeight: '400px', maxHeight: '800px' } : { height: '400px' })
        }}>
          {renderChart()}
        </div>
      )}
    </Card>
  );
};

export default DataChart;

